import { useMemo } from 'react';
import { X } from 'lucide-react';
import type { VendorCategoryInfo } from '@peles/shared';
import {
  useAddVendorCategory,
  useRemoveVendorCategory,
  useVendorCategories,
} from '../../hooks/useVendors';
import { useDialog } from '../../context/DialogContext';
import { extractApiErrorMessage } from '../../utils/apiError';
import { SearchableListSelect } from '../common/SearchableListSelect';

interface VendorCategoryChipsProps {
  vendorId: string;
  categories: VendorCategoryInfo[];
  /** Offer adding and removing; read-only chips otherwise. */
  canManage: boolean;
}

/**
 * A vendor's categories as chips, each removable, with a picker to add one.
 * Saves at once. The vendor page and the vendor edit form each had their own
 * copy — one a plain `<select>` that closed on blur.
 */
export function VendorCategoryChips({ vendorId, categories, canManage }: VendorCategoryChipsProps) {
  const { data: allCategories = [] } = useVendorCategories();
  const addCategory = useAddVendorCategory(vendorId);
  const removeCategory = useRemoveVendorCategory(vendorId);
  const { alert } = useDialog();

  const available = useMemo(() => {
    const assigned = new Set(categories.map((c) => c.vendorCategoryId));
    return allCategories.filter((c) => c.isActive && !assigned.has(c.id));
  }, [allCategories, categories]);

  const failed = (title: string) => (err: unknown) =>
    void alert({
      title,
      message: extractApiErrorMessage(err, 'Please try again.'),
      variant: 'error',
    });

  return (
    <div className="flex flex-wrap items-center gap-2">
      {categories.length === 0 && <p className="text-xs text-gray-400">No categories assigned</p>}
      {categories.map((c) => (
        <span
          key={c.id}
          className="inline-flex items-center gap-1 pl-3 pr-1 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
        >
          {c.categoryName}
          {c.isPrimary && (
            <span className="ml-1 px-1.5 py-0.5 bg-primary-200 text-primary-800 rounded-full text-[10px] leading-none">
              Primary
            </span>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() =>
                removeCategory.mutate(c.vendorCategoryId, {
                  onError: failed('Category not removed'),
                })
              }
              disabled={removeCategory.isPending}
              // 32px drawn, 44px to tap: the chip stays a chip.
              className="relative min-h-8 min-w-8 flex items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-[''] text-primary-500 hover:text-primary-800 hover:bg-primary-200 disabled:opacity-50"
              aria-label={`Remove ${c.categoryName}`}
            >
              <X size={12} />
            </button>
          )}
        </span>
      ))}
      {canManage && available.length > 0 && (
        <div className="w-full sm:w-56">
          <SearchableListSelect
            items={available}
            value={null}
            onChange={(id) => {
              if (typeof id === 'string' && id) {
                addCategory.mutate(
                  { vendorCategoryId: id },
                  { onError: failed('Category not added') },
                );
              }
            }}
            getValue={(c) => c.id}
            getLabel={(c) => c.name}
            placeholder="Add category..."
            ariaLabel="Add category"
            disabled={addCategory.isPending}
            clearable={false}
          />
        </div>
      )}
    </div>
  );
}
